import { useState } from 'react';
import { FileText, X, Upload, ExternalLink, Loader2, Download } from 'lucide-react';
import { useResumeUrl, useUploadResume } from '../hooks/useApi';

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.txt,.rtf';

interface ResumeViewerProps {
  applicantId: string;
  resumeKey: string | null;
  applicantName: string;
}

export default function ResumeViewer({ applicantId, resumeKey, applicantName }: ResumeViewerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const resumeUrl = useResumeUrl(applicantId);
  const uploadResume = useUploadResume();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');

    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const allowedExts = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
    if (!allowedExts.includes(ext)) {
      setUploadError('Only PDF, Word, Text, and RTF files are allowed.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File must be under 10MB.');
      return;
    }

    setIsUploading(true);
    try {
      await uploadResume.mutateAsync({ applicantId, file });
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const getFileInfo = () => {
    if (!resumeKey) return null;
    const ext = resumeKey.split('.').pop()?.toLowerCase() || '';
    const filename = resumeKey.split('/').pop() || 'resume';
    return { ext, filename };
  };

  const fileInfo = getFileInfo();
  const isPdf = fileInfo?.ext === 'pdf';
  const isText = fileInfo?.ext === 'txt';

  if (!resumeKey) {
    return (
      <div>
        <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <FileText className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-500 flex-1">No resume uploaded</span>
          <label className="btn-secondary text-sm cursor-pointer">
            {isUploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Upload className="w-4 h-4 mr-1" />
                Upload
              </>
            )}
            <input
              type="file"
              accept={ACCEPTED_TYPES}
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
          </label>
        </div>
        {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
        <p className="text-xs text-gray-400 mt-1">PDF, Word, Text, RTF — max 10MB</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
          <FileText className="w-5 h-5 text-green-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-green-800 truncate">{fileInfo?.filename}</p>
            <p className="text-xs text-green-600 uppercase">{fileInfo?.ext}</p>
          </div>
          <button
            onClick={() => isPdf || isText ? setIsOpen(true) : window.open(resumeUrl, '_blank')}
            className="p-1.5 hover:bg-green-100 rounded text-green-700"
            title={isPdf || isText ? 'View' : 'Download'}
          >
            {isPdf || isText ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
          </button>
          <label className="p-1.5 hover:bg-green-100 rounded text-green-700 cursor-pointer" title="Replace">
            <Upload className="w-4 h-4" />
            <input
              type="file"
              accept={ACCEPTED_TYPES}
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
          </label>
        </div>
        {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
      </div>

      {/* File Viewer Modal — PDF and text render inline, others download */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {applicantName} - Resume
                </h3>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full uppercase font-medium">
                  {fileInfo?.ext}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={resumeUrl}
                  download
                  className="btn-secondary text-sm inline-flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
                <a
                  href={resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  New Tab
                </a>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 bg-gray-100 overflow-hidden">
              {isPdf ? (
                <iframe
                  src={resumeUrl}
                  className="w-full h-full"
                  title={`${applicantName} Resume`}
                />
              ) : isText ? (
                <TextFileViewer url={resumeUrl} />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <FileText className="w-16 h-16 text-gray-300" />
                  <p className="text-gray-500">
                    {fileInfo?.ext?.toUpperCase()} files can't be previewed in the browser.
                  </p>
                  <div className="flex gap-3">
                    <a
                      href={resumeUrl}
                      download
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download File
                    </a>
                    <a
                      href={`https://docs.google.com/gview?url=${encodeURIComponent(resumeUrl)}&embedded=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary inline-flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open in Google Docs Viewer
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Renders plain text files inline
function TextFileViewer({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useState(() => {
    fetch(url)
      .then(res => res.text())
      .then(text => { setContent(text); setLoading(false); })
      .catch(() => { setContent('Failed to load file.'); setLoading(false); });
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <pre className="p-6 text-sm text-gray-800 whitespace-pre-wrap font-mono overflow-y-auto h-full">
      {content}
    </pre>
  );
}
